import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { palette } from '../../theme';

/** Smoothly counts a numeric value up to its target for premium data reveals. */
export function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  const frameRef = useRef(null);
  const previous = useRef(0);

  useEffect(() => {
    const numeric = Number(target);
    if (!Number.isFinite(numeric)) {
      setValue(target);
      return undefined;
    }
    const from = previous.current;
    const delta = numeric - from;
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(from + delta * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        previous.current = numeric;
      }
    };

    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration]);

  return value;
}

/**
 * Floating 3D metric card.
 * The surface lifts in perspective on hover and carries a volumetric glow
 * beneath it to read as an object floating above the console surface.
 */
export function StatCard3D({
  title,
  value,
  suffix = '',
  icon,
  accent = palette.primary,
  caption,
  progress,
  trend,
  onClick,
  compact = false,
}) {
  const [hovered, setHovered] = useState(false);
  const animated = useCountUp(value);
  const isNumeric = Number.isFinite(Number(value)) && value !== null && value !== undefined;

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (event) => event.key === 'Enter' && onClick(event) : undefined}
      sx={{
        position: 'relative',
        perspective: 1100,
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        '&:focus-visible': { outline: `2px solid ${alpha(accent, 0.6)}`, outlineOffset: 3 },
      }}
    >
      {/* volumetric shadow / light pool beneath the floating card */}
      <Box
        sx={{
          position: 'absolute',
          left: '12%',
          right: '12%',
          bottom: -14,
          height: 34,
          borderRadius: '50%',
          background: `radial-gradient(closest-side, ${alpha(accent, hovered ? 0.4 : 0.2)}, transparent)`,
          filter: 'blur(12px)',
          transition: 'opacity 320ms ease, transform 320ms ease',
          opacity: hovered ? 1 : 0.65,
          transform: `scale(${hovered ? 1.12 : 1})`,
        }}
      />

      <Box
        sx={{
          position: 'relative',
          height: '100%',
          borderRadius: 4,
          padding: compact ? 2.25 : 2.75,
          border: `1px solid ${alpha(accent, hovered ? 0.4 : 0.16)}`,
          background: `linear-gradient(155deg, ${alpha('#101B36', 0.82)}, ${alpha('#070C1C', 0.86)})`,
          backdropFilter: 'blur(20px)',
          transform: hovered
            ? 'rotateX(6deg) rotateY(-5deg) translateY(-6px) scale(1.012)'
            : 'rotateX(0deg) rotateY(0deg) translateY(0)',
          transformStyle: 'preserve-3d',
          transition: 'transform 420ms cubic-bezier(0.22, 0.9, 0.28, 1), border-color 320ms ease, box-shadow 320ms ease',
          boxShadow: hovered
            ? `0 30px 70px -30px rgba(2,6,23,0.98), inset 0 1px 0 ${alpha('#FFFFFF', 0.07)}`
            : `0 20px 50px -28px rgba(2,6,23,0.95), inset 0 1px 0 ${alpha('#FFFFFF', 0.04)}`,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: `radial-gradient(130% 100% at 0% 0%, ${alpha(accent, 0.16)}, transparent 58%)`,
          },
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          <Typography
            sx={{
              fontSize: '0.68rem',
              fontWeight: 700,
              letterSpacing: '0.16em',
              color: 'text.secondary',
              textTransform: 'uppercase',
              lineHeight: 1.4,
            }}
          >
            {title}
          </Typography>

          {icon && (
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: compact ? 34 : 40,
                height: compact ? 34 : 40,
                borderRadius: 2.5,
                color: accent,
                background: alpha(accent, 0.12),
                border: `1px solid ${alpha(accent, 0.3)}`,
                boxShadow: `inset 0 0 20px ${alpha(accent, 0.18)}, 0 6px 18px -10px ${alpha(accent, 0.9)}`,
                transform: hovered ? 'translateZ(26px)' : 'translateZ(0)',
                transition: 'transform 420ms cubic-bezier(0.22, 0.9, 0.28, 1)',
                flexShrink: 0,
                '& svg': { fontSize: compact ? 18 : 21 },
              }}
            >
              {icon}
            </Box>
          )}
        </Box>

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 1.5 }}>
          <Typography
            className="mono"
            sx={{
              fontSize: compact ? '1.6rem' : '2.1rem',
              fontWeight: 500,
              lineHeight: 1,
              color: 'text.primary',
              textShadow: `0 0 26px ${alpha(accent, 0.45)}`,
            }}
          >
            {isNumeric ? animated : value}
          </Typography>
          {suffix && (
            <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', fontWeight: 600 }}>{suffix}</Typography>
          )}
          {trend && (
            <Typography
              sx={{
                ml: 'auto',
                fontSize: '0.7rem',
                fontWeight: 700,
                color: trend >= 0 ? palette.success : palette.danger,
                letterSpacing: '0.04em',
              }}
            >
              {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%
            </Typography>
          )}
        </Box>

        {progress !== undefined && progress !== null && (
          <Box sx={{ position: 'relative', mt: 1.75 }}>
            <Box sx={{ height: 5, borderRadius: 4, background: alpha(accent, 0.12), overflow: 'hidden' }}>
              <Box
                sx={{
                  height: '100%',
                  width: `${Math.max(0, Math.min(100, progress))}%`,
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${alpha(accent, 0.7)}, ${accent})`,
                  boxShadow: `0 0 14px ${alpha(accent, 0.6)}`,
                  transition: 'width 900ms cubic-bezier(0.22, 0.9, 0.28, 1)',
                }}
              />
            </Box>
          </Box>
        )}

        {caption && (
          <Typography
            sx={{
              position: 'relative',
              mt: progress !== undefined && progress !== null ? 1.1 : 1,
              fontSize: '0.72rem',
              color: 'text.secondary',
              lineHeight: 1.45,
            }}
          >
            {caption}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default StatCard3D;
